import React, { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";

import ValidateProcurementSupplierForm, {
  ValidateProcurementSupplierFormValues,
} from "./ValidateProcurementSupplierForm";

import { useValidateProcurementSupplier } from "@api";
import { useRegisterSupplierStore } from "@store/registerSupplierStore";

type ValidateFormContainerProps = {
  companyId: string;
  onSuccess: (data: ValidateProcurementSupplierFormValues) => void;
};

const ValidateFormContainer: FC<ValidateFormContainerProps> = ({
  companyId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { validatedData, setValidatedData } = useRegisterSupplierStore();

  const schema = yup.object({
    companyName: yup.string().trim().required(t("validation.companyName")),
    country: yup.string().trim().required(t("validation.country")),
    companyId: yup.string().required(),
    vat: yup.string().trim().max(64).required(t("validation.vatIsRequired")),
  });

  const { control, handleSubmit, setError, reset } =
    useForm<ValidateProcurementSupplierFormValues>({
      defaultValues: {
        companyName: "",
        country: "",
        companyId,
        vat: "",
      },
      resolver: yupResolver(schema),
      mode: "onChange",
    });

  useEffect(() => {
    if (validatedData) {
      reset({
        companyName: validatedData.companyName,
        country: validatedData.country,
        companyId: companyId,
        vat: validatedData.vat,
      });
    }
  }, [validatedData, reset, companyId]);

  const { validateSupplierAsync } = useValidateProcurementSupplier(setError);

  const onSubmit = async (
    values: ValidateProcurementSupplierFormValues,
  ): Promise<void> => {
    await validateSupplierAsync({ companyId, vat: values.vat }).then(() => {
      setValidatedData(values);
      onSuccess(values);
    });
  };

  return (
    <ValidateProcurementSupplierForm
      onSubmit={handleSubmit(onSubmit)}
      control={control}
    />
  );
};

export default ValidateFormContainer;
